import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import { useHandleShowDialogs } from 'forms/hooks';
import { useDispatch, useSelector } from 'react-redux';

import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import { validationDisplayTypes } from '@gef-ui/form-material/constants';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components/index';
import { MaxLengths } from 'constants/forms';
import fetch from 'features/common/fetch';
import { addSingleErrorByPageAndId, removeErrorByPageAndId } from 'features/common/submitFormDialog/slice';
import { saveCampaignTemplate } from 'features/views/campaignSettings/templates/actions';
import { FIELDS, SAVE_TEMPLATE_FORM_NAME } from 'features/views/campaignSettings/templates/form';
import { getMaxTemplatesCount, getTemplateTable } from 'features/views/campaignSettings/templates/selectors';

export const SaveTemplateForm = ({ campaignId }) => {
	const dispatch = useDispatch();
	const formData = useFormData();
	const maxTemplatesCount = useSelector(getMaxTemplatesCount);
	const templatesList = useSelector(getTemplateTable);
	const maxTemplatesCountReached = templatesList.length >= maxTemplatesCount;

	const handleSaveTemplate = useCallback(() => {
		dispatch(removeErrorByPageAndId({ page: SAVE_TEMPLATE_FORM_NAME, id: FIELDS.templateName }));
		dispatch(saveCampaignTemplate(campaignId));
	}, [dispatch, campaignId]);

	// only validationg form fields
	const handleShowDialogsForFormFields = useHandleShowDialogs({
		page: SAVE_TEMPLATE_FORM_NAME,
		continueFn: handleSaveTemplate,
	});

	// final handler = form fields validation + custom maxTemplateAmount validation
	const handleShowDialogsForSubmit = useCallback(
		(formData, e) => {
			handleShowDialogsForFormFields(formData, e);

			if (maxTemplatesCountReached) {
				dispatch(
					addSingleErrorByPageAndId({
						page: SAVE_TEMPLATE_FORM_NAME,
						errors: [{ name: 'saveTemplateMaxAmountReached', params: { maxCount: maxTemplatesCount } }],
					}),
				);
			}
		},
		[dispatch, handleShowDialogsForFormFields, maxTemplatesCountReached, maxTemplatesCount],
	);

	return (
		<ComponentLoader fetchStorePath={fetch.constants.urls.campaignPattern} ignoreErrors>
			<ComponentLoader fetchStorePath={fetch.constants.urls.campaignLoadPattern} ignoreErrors>
				<Grid container spacing={2}>
					<Grid size={12}>
						<Grid container justifyContent="center">
							<Grid size={4}>
								<TextField
									size="small"
									fieldName={FIELDS.templateName}
									label={getLabelMessage('templates:templateName')}
									required
									validationDisplay={
										!formData?.templateName ? validationDisplayTypes.HIDDEN : undefined
									}
									slotProps={{
										htmlInput: {
											maxLength: MaxLengths.CAMPAIGN_TEMPLATE_NAME,
										},
									}}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid size={12}>
						<Grid container justifyContent="center">
							<Grid size={4}>
								<SubmitButton
									variant="contained"
									onClick={handleSaveTemplate}
									onClickError={handleShowDialogsForSubmit}
									fullWidth
									text={getLabelMessage('common:save')}
								/>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</ComponentLoader>
		</ComponentLoader>
	);
};
SaveTemplateForm.propTypes = {
	campaignId: PropTypes.number,
};

export default SaveTemplateForm;
